import { lightFormat } from 'date-fns';
import { BulkChangeBillingPreviewModel } from '@lib/state/api/generated/reservations';
import { Reservation } from '@lib/state';

// ---------------------------------------------------------------------------
// Preview filter types
// ---------------------------------------------------------------------------
export type PreviewFilter = 'all' | 'increases' | 'decreases' | 'noChange' | 'blocked';

// ---------------------------------------------------------------------------
// Row classification helpers
// ---------------------------------------------------------------------------

/** Returns true when the reservation is ineligible for a billing change. */
export function isRowBlocked(row: BulkChangeBillingPreviewModel): boolean {
  return row.isEligibleForChange === false;
}

/** Classify a preview row into a filter bucket. */
export function classifyRow(row: BulkChangeBillingPreviewModel): PreviewFilter {
  if (isRowBlocked(row)) return 'blocked';
  const net = row.netDifference ?? 0;
  if (net > 0) return 'increases';
  if (net < 0) return 'decreases';
  return 'noChange';
}

/** Check whether a row matches the active preview filter. */
export function matchesFilter(row: BulkChangeBillingPreviewModel, filter: PreviewFilter): boolean {
  return filter === 'all' || classifyRow(row) === filter;
}

// ---------------------------------------------------------------------------
// Currency formatting
// ---------------------------------------------------------------------------

/** Format a dollar amount with commas and two decimal places. Returns em-dash for null/undefined. */
export function formatCurrency(value?: number | null): string {
  if (value == null) return '—';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** Format a signed dollar amount (+$1,234.56 / -$1,234.56). Returns em-dash for null/undefined. */
export function formatSignedCurrency(value?: number | null): string {
  if (value == null) return '—';
  const formatted = Math.abs(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
}

// ---------------------------------------------------------------------------
// Row status derivation
// ---------------------------------------------------------------------------

export type RowStatusVariant = 'blocked' | 'increase' | 'decrease' | 'ready';

export interface RowStatus {
  label: string;
  shortText: string;
  detail: string;
  variant: RowStatusVariant;
}

/** Derive a short status label + full detail text for a preview row. */
export function getRowStatus(row: BulkChangeBillingPreviewModel): RowStatus {
  if (isRowBlocked(row)) {
    const shortReason = row.warnings
      ? row.warnings.length > 40
        ? row.warnings.slice(0, 37) + '…'
        : row.warnings
      : 'Not eligible';
    return {
      label: 'Blocked',
      shortText: shortReason,
      detail: row.warnings ?? 'This reservation is not eligible for change.',
      variant: 'blocked',
    };
  }
  const net = row.netDifference ?? 0;
  if (net > 0) {
    return {
      label: 'Increase',
      shortText: formatSignedCurrency(net),
      detail: row.warnings ?? `Price will increase by ${formatCurrency(net)}.`,
      variant: 'increase',
    };
  }
  if (net < 0) {
    return {
      label: 'Decrease',
      shortText: formatSignedCurrency(net),
      detail: row.warnings ?? `Price will decrease by ${formatCurrency(Math.abs(net))}.`,
      variant: 'decrease',
    };
  }
  return {
    label: 'No Change',
    shortText: 'Ready',
    detail: row.warnings ?? 'No financial impact.',
    variant: 'ready',
  };
}

// ---------------------------------------------------------------------------
// Range-restricted rate helpers
// ---------------------------------------------------------------------------

/**
 * Get rates from `res.rooms[0]` whose `date` falls within
 * [start, end] (inclusive). Date comparison is done as ISO yyyy-MM-dd strings.
 */
function getInRangeRates(reservation: Reservation, start: Date, end: Date) {
  const room = reservation.rooms?.[0];
  if (!room?.rates?.length) return [];
  const startStr = lightFormat(start, 'yyyy-MM-dd');
  const endStr = lightFormat(end, 'yyyy-MM-dd');
  return room.rates.filter(r => r.date >= startStr && r.date <= endStr);
}

/**
 * Compute the arithmetic mean of nightly rate amounts for `res.rooms[0]`
 * restricted to nights within the given effective date range (inclusive).
 * Returns undefined when there are no rates in range.
 */
export function getAverageRateInRange(
  reservation: Reservation,
  start: Date,
  end: Date
): number | undefined {
  const inRange = getInRangeRates(reservation, start, end);
  if (inRange.length === 0) return undefined;
  const total = inRange.reduce((sum, r) => sum + (r.amount ?? 0), 0);
  return total / inRange.length;
}

/**
 * Returns the distinct nightly rate amounts (de-duplicated) for `res.rooms[0]`
 * within the given effective date range (inclusive). Sorted ascending.
 */
export function getInRangeRateAmounts(reservation: Reservation, start: Date, end: Date): number[] {
  const inRange = getInRangeRates(reservation, start, end);
  const set = new Set<number>();
  for (const r of inRange) {
    if (r.amount != null) set.add(r.amount);
  }
  return [...set].sort((a, b) => a - b);
}

/**
 * Returns the rate plan name shared by all in-range rates, or
 * 'Multiple plans' if rates use more than one plan, or undefined
 * if there are no in-range rates.
 */
export function getRatePlanForRange(
  reservation: Reservation,
  start: Date,
  end: Date
): string | undefined {
  const inRange = getInRangeRates(reservation, start, end);
  if (inRange.length === 0) return undefined;
  const distinct = new Set(inRange.map(r => r.ratePlan).filter(Boolean));
  if (distinct.size === 0) return undefined;
  if (distinct.size === 1) return [...distinct][0];
  return 'Multiple plans';
}
